/**
 * Offline punch queue.
 *
 * When a clock-in/out can't reach the server (no signal, or a network error), the punch
 * is kept on the phone and sent later — on app start, when the phone comes back online,
 * and whenever the app is brought to the front. iOS has no background sync, so the app
 * has to be opened for queued punches to go.
 *
 * A punch is also saved when the server doesn't answer within PUNCH_TIMEOUT_MS, or when
 * the cleaner stops waiting: it may have got through, which is fine, because each keeps
 * its original clientId and the server ignores duplicates.
 *
 * Queued punches are always sent with `offline: true`, the device capture time and
 * `requestApproval: true`, so a punch that would have been blocked becomes "pending
 * approval" instead of being lost.
 *
 * Each punch remembers who was signed in when it was saved, and is only sent while the
 * server says that same person is signed in (asked with GET /me just before sending).
 * On a shared or handed-on phone, someone else's punches are kept, not sent as you.
 *
 * Storage: IndexedDB, falling back to localStorage (e.g. if IndexedDB is unavailable).
 * Reads merge both, so nothing is stranded if one of them fails half-way.
 */
import type { Me, PunchRequest, PunchResponse } from "../../shared/types";
import { ApiError, api } from "./api";

export type PunchKind = "in" | "out";

export interface QueuedPunch {
  clientId: string;
  kind: PunchKind;
  req: PunchRequest;
  /**
   * Who was signed in when it was saved. It is only ever sent while that same person is
   * signed in, so a punch can't end up on someone else's timesheet on a shared phone.
   * Missing on punches saved by older versions of the app: those go with whoever is
   * signed in, as before.
   */
  userId?: string | null;
  /** Site name if known on the phone (for display only). */
  siteName: string | null;
  queuedAt: number;
  attempts: number;
  /** The last server answer that kept it on the phone (for display). */
  lastError?: { status: number; message: string } | null;
}

export interface FlushOutcome {
  item: QueuedPunch;
  /** The server's answer, when it gave a PunchResponse. */
  response?: PunchResponse;
  /** The server refused the request (e.g. "You're not clocked in"). */
  error?: { status: number; message: string };
}

/** Why a flush stopped with some of this person's punches still on the phone. */
export interface FlushStop {
  /** offline/network: no usable signal. signed_out: no session. server: the server failed. */
  reason: "offline" | "network" | "signed_out" | "server";
  /** The punch that couldn't be sent (absent when we never got as far as sending). */
  item?: QueuedPunch;
  status?: number;
  message?: string;
}

export interface FlushResult {
  outcomes: FlushOutcome[];
  /** Null when nothing of the signed-in person's is left on the phone. */
  stopped: FlushStop | null;
  /** The signed-in person's punches still waiting. */
  remaining: number;
  /** Punches on this phone that belong to someone else: kept, never sent for this person. */
  othersWaiting: number;
}

/** How long to wait for the server before treating a punch as "no signal" (and saving it). */
export const PUNCH_TIMEOUT_MS = 20_000;

/**
 * Runs `fn` with an AbortSignal that fires after `timeoutMs` or when `signal` aborts.
 * Aborts surface as ApiError status 0 with code "timeout" or "cancelled", so callers
 * treat them like any other network failure.
 */
async function withTimeout<T>(
  fn: (signal: AbortSignal) => Promise<T>,
  timeoutMs: number,
  signal?: AbortSignal,
): Promise<T> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  const onAbort = () => ctrl.abort();
  if (signal?.aborted) ctrl.abort();
  else signal?.addEventListener("abort", onAbort, { once: true });
  try {
    return await fn(ctrl.signal);
  } catch (e) {
    if (ctrl.signal.aborted && (isNetworkError(e) || !(e instanceof ApiError))) {
      throw signal?.aborted
        ? new ApiError(0, "cancelled", "Stopped waiting for the server.")
        : new ApiError(0, "timeout", "The signal is too weak to reach the office right now.");
    }
    throw e;
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener("abort", onAbort);
  }
}

/**
 * Sends a punch to the server. Throws ApiError (status 0 for network failures, a
 * timeout after PUNCH_TIMEOUT_MS, or `signal` aborting).
 */
export function sendPunch(
  kind: PunchKind,
  req: PunchRequest,
  opts: { signal?: AbortSignal; timeoutMs?: number } = {},
): Promise<PunchResponse> {
  return withTimeout(
    (signal) => api<PunchResponse>(`/clock/${kind}`, { method: "POST", json: req, signal }),
    opts.timeoutMs ?? PUNCH_TIMEOUT_MS,
    opts.signal,
  );
}

export function isNetworkError(e: unknown): boolean {
  return e instanceof ApiError && e.status === 0;
}

/** Server answers worth trying again later rather than dropping the punch. */
function isRetryable(e: unknown): boolean {
  if (!(e instanceof ApiError)) return false;
  // 401: signed out on this phone — keep the punch until they sign back in.
  return e.status === 0 || e.status === 401 || e.status === 408 || e.status === 429 || e.status >= 500;
}

/** True when the punch may be sent for (and shown to) the person with this user id. */
export function belongsTo(item: QueuedPunch, userId: string | null | undefined): boolean {
  if (!userId) return false;
  return item.userId == null || item.userId === userId;
}

// --- Storage ---------------------------------------------------------------

const DB_NAME = "clockin";
const STORE = "punchQueue";
const LS_KEY = "clockin.punchQueue";

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  dbPromise ??= new Promise<IDBDatabase>((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("IndexedDB unavailable"));
      return;
    }
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE)) {
        req.result.createObjectStore(STORE, { keyPath: "clientId" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("IndexedDB open failed"));
    req.onblocked = () => reject(new Error("IndexedDB blocked"));
  }).catch((e) => {
    dbPromise = null;
    throw e;
  });
  return dbPromise;
}

async function idb<T>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await openDb();
  return new Promise<T>((resolve, reject) => {
    const tx = db.transaction(STORE, mode);
    const req = fn(tx.objectStore(STORE));
    tx.oncomplete = () => resolve(req.result);
    tx.onerror = () => reject(tx.error ?? new Error("IndexedDB error"));
    tx.onabort = () => reject(tx.error ?? new Error("IndexedDB aborted"));
  });
}

function lsRead(): QueuedPunch[] {
  try {
    const v = JSON.parse(localStorage.getItem(LS_KEY) ?? "[]");
    return Array.isArray(v) ? (v as QueuedPunch[]) : [];
  } catch {
    return [];
  }
}

function lsWrite(items: QueuedPunch[]): void {
  try {
    if (items.length) localStorage.setItem(LS_KEY, JSON.stringify(items));
    else localStorage.removeItem(LS_KEY);
  } catch {
    // Nothing more we can do.
  }
}

async function save(item: QueuedPunch): Promise<void> {
  try {
    await idb("readwrite", (s) => s.put(item));
  } catch {
    const items = lsRead().filter((i) => i.clientId !== item.clientId);
    items.push(item);
    lsWrite(items);
  }
}

async function remove(clientId: string): Promise<void> {
  try {
    await idb("readwrite", (s) => s.delete(clientId));
  } catch {
    // Fall through: it may only be in localStorage.
  }
  const ls = lsRead();
  if (ls.some((i) => i.clientId === clientId)) lsWrite(ls.filter((i) => i.clientId !== clientId));
}

/** All queued punches, oldest capture first (so a clock-in is sent before its clock-out). */
export async function listQueued(): Promise<QueuedPunch[]> {
  let fromDb: QueuedPunch[] = [];
  try {
    fromDb = await idb<QueuedPunch[]>("readonly", (s) => s.getAll() as IDBRequest<QueuedPunch[]>);
  } catch {
    fromDb = [];
  }
  const byId = new Map<string, QueuedPunch>();
  for (const i of [...lsRead(), ...fromDb]) byId.set(i.clientId, i);
  return [...byId.values()].sort(
    (a, b) => (a.req.capturedAt ?? a.queuedAt) - (b.req.capturedAt ?? b.queuedAt),
  );
}

// --- Change notifications ---------------------------------------------------

const events = new EventTarget();

/** Called whenever the queue changes. */
export function onQueueChange(fn: () => void): () => void {
  events.addEventListener("change", fn);
  return () => events.removeEventListener("change", fn);
}

/** Called with the server's answers after queued punches are sent. */
export function onFlushed(fn: (outcomes: FlushOutcome[]) => void): () => void {
  const h = (e: Event) => fn((e as CustomEvent<FlushOutcome[]>).detail);
  events.addEventListener("flushed", h);
  return () => events.removeEventListener("flushed", h);
}

const changed = () => events.dispatchEvent(new Event("change"));

let flushedCount = 0;

/**
 * How many flushes so far (in this page load) sent or dropped something. Lets a screen
 * that mounts after a flush (e.g. the first flush on app start, before the session has
 * loaded) know it has missed one and should refresh.
 */
export function flushGeneration(): number {
  return flushedCount;
}

// --- Flush results kept until the cleaner has seen them ----------------------
//
// A flush often finishes while nothing is listening (on app start, or on another tab of
// the app), so its results are kept on the phone, per person, until dismissed.

const NOTES_KEY = "clockin.flushNotes";
const MAX_NOTES = 20;

interface StoredNote {
  userId: string;
  outcome: FlushOutcome;
}

function readNotes(): StoredNote[] {
  try {
    const v = JSON.parse(localStorage.getItem(NOTES_KEY) ?? "[]");
    return Array.isArray(v) ? (v as StoredNote[]) : [];
  } catch {
    return [];
  }
}

function writeNotes(notes: StoredNote[]): void {
  try {
    if (notes.length) localStorage.setItem(NOTES_KEY, JSON.stringify(notes.slice(-MAX_NOTES)));
    else localStorage.removeItem(NOTES_KEY);
  } catch {
    // Storage blocked: the live "flushed" event is all we have.
  }
}

/** Results of saved punches sent for this person that they haven't dismissed yet. */
export function flushNotesFor(userId: string): FlushOutcome[] {
  return readNotes()
    .filter((n) => n.userId === userId)
    .map((n) => n.outcome);
}

/** Forgets this person's flush results (all of them, or just the given punches). */
export function dismissFlushNotes(userId: string, clientIds?: string[]): void {
  const before = readNotes();
  const after = before.filter(
    (n) => n.userId !== userId || (clientIds !== undefined && !clientIds.includes(n.outcome.item.clientId)),
  );
  if (after.length !== before.length) {
    writeNotes(after);
    events.dispatchEvent(new Event("notes"));
  }
}

/** Called when the stored flush results change (added or dismissed). */
export function onFlushNotesChange(fn: () => void): () => void {
  events.addEventListener("notes", fn);
  return () => events.removeEventListener("notes", fn);
}

// --- Public API ------------------------------------------------------------

/**
 * Stores a punch to send later. `capturedAt` should be when the cleaner actually
 * scanned, so the server records the real time. `userId` is the person signed in now:
 * the punch will only be sent while they are signed in.
 */
export async function queuePunch(
  kind: PunchKind,
  req: PunchRequest,
  opts: { siteName: string | null; userId: string; capturedAt?: number },
): Promise<QueuedPunch> {
  const capturedAt = opts.capturedAt ?? Date.now();
  const item: QueuedPunch = {
    clientId: req.clientId,
    kind,
    req: { ...req, offline: true, capturedAt, requestApproval: true },
    userId: opts.userId,
    siteName: opts.siteName,
    queuedAt: Date.now(),
    attempts: 0,
    lastError: null,
  };
  await save(item);
  changed();
  return item;
}

/** Deletes a saved punch without sending it. */
export async function discardQueued(clientId: string): Promise<void> {
  await remove(clientId);
  changed();
}

let flushing: Promise<FlushResult> | null = null;

/**
 * Sends the signed-in person's queued punches in order. Stops at the first failure
 * worth retrying (keeping the rest, in order). Never throws.
 */
export function flushQueue(): Promise<FlushResult> {
  flushing ??= doFlush().finally(() => {
    flushing = null;
  });
  return flushing;
}

const ME_TIMEOUT_MS = 15_000;

/** Who the server says is signed in right now: the person queued punches would be sent as. */
async function currentUserId(): Promise<string> {
  const me = await withTimeout((signal) => api<Me>("/me", { signal }), ME_TIMEOUT_MS);
  return me.user.id;
}

function stopFor(e: unknown, item?: QueuedPunch): FlushStop {
  const err = e instanceof ApiError ? e : null;
  const status = err?.status ?? 0;
  const reason: FlushStop["reason"] =
    status === 0 ? "network" : status === 401 || status === 403 ? "signed_out" : "server";
  return { reason, item, status, message: err?.message ?? "Couldn't be sent." };
}

async function doFlush(): Promise<FlushResult> {
  const outcomes: FlushOutcome[] = [];
  let items: QueuedPunch[];
  try {
    items = await listQueued();
  } catch {
    return { outcomes, stopped: null, remaining: 0, othersWaiting: 0 };
  }
  if (!items.length) return { outcomes, stopped: null, remaining: 0, othersWaiting: 0 };

  // Ask the server who this phone is signed in as: only their punches are sent. Someone
  // else's (a shared or handed-on phone) are kept until that person signs in again.
  let userId: string;
  try {
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      throw new ApiError(0, "offline", "No signal.");
    }
    userId = await currentUserId();
  } catch (e) {
    const stop = stopFor(e);
    if (e instanceof ApiError && e.code === "offline") stop.reason = "offline";
    // We can't tell whose they are, so report them all as waiting.
    return { outcomes, stopped: stop, remaining: items.length, othersWaiting: 0 };
  }
  const mine = items.filter((i) => belongsTo(i, userId));
  const othersWaiting = items.length - mine.length;

  let stopped: FlushStop | null = null;
  let done = 0;
  for (const item of mine) {
    try {
      const response = await sendPunch(item.kind, item.req);
      await remove(item.clientId);
      outcomes.push({ item, response });
    } catch (e) {
      if (isRetryable(e)) {
        stopped = stopFor(e, item);
        if (!isNetworkError(e)) {
          const lastError = { status: stopped.status ?? 0, message: stopped.message ?? "" };
          await save({ ...item, attempts: item.attempts + 1, lastError });
          changed();
        }
        break; // keep order: don't send a clock-out ahead of its clock-in
      }
      // The server answered (e.g. 409 "already clocked in"): sending again won't help.
      await remove(item.clientId);
      const err = e instanceof ApiError ? e : null;
      outcomes.push({
        item,
        error: { status: err?.status ?? 0, message: err?.message ?? "Couldn't be sent." },
      });
    }
    done++;
  }
  if (outcomes.length) {
    flushedCount++;
    writeNotes([...readNotes(), ...outcomes.map((outcome) => ({ userId, outcome }))]);
    changed();
    events.dispatchEvent(new Event("notes"));
    events.dispatchEvent(new CustomEvent("flushed", { detail: outcomes }));
  }
  return { outcomes, stopped, remaining: mine.length - done, othersWaiting };
}

/** While punches are waiting, try again this often (if the app is open and in front). */
const RETRY_EVERY_MS = 30_000;

let autoFlushStarted = false;

/**
 * Flushes now, then whenever the phone comes online, the app becomes visible, and every
 * 30 s while it is in front. Idempotent: safe to call from every page.
 */
export function startAutoFlush(): void {
  if (autoFlushStarted || typeof window === "undefined") return;
  autoFlushStarted = true;
  const kick = () => void flushQueue();
  window.addEventListener("online", kick);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") kick();
  });
  setInterval(() => {
    if (document.visibilityState === "visible") kick();
  }, RETRY_EVERY_MS);
  kick();
}
